import { SafetyRating } from '@graphql/generated-base';
import { useEventSafetyRatingQuery } from './useEventSafetyRating.graphql.generated';

type UseEventSafetyRatingArgs = Readonly<{
  eventHandle: Maybe<string>;
}>;

type UseEventSafetyRatingResult = Readonly<{
  /** True only when the event is flagged `unsafeRequiresEscalation`. */
  isUnsafe: boolean;
  isLoading: boolean;
}>;

/**
 * Fetches an event's `SafetyRating` (by handle) and reports whether it is unsafe.
 *
 * Single source of truth for the trust & safety gate shared by the messaging
 * Send flow ([[useVerifyEventSafetyAndSendMessage]]) and the AI text review
 * feature. Keyed by `eventHandle` so all consumers reuse the same
 * `EventSafetyRating` query and its Apollo cache entry.
 */
export const useEventSafetyRating = ({ eventHandle }: UseEventSafetyRatingArgs): UseEventSafetyRatingResult => {
  const { data, loading } = useEventSafetyRatingQuery({
    variables: {
      name: eventHandle!,
    },
    skip: !eventHandle,
    batchMode: 'fast',
  });

  const isUnsafe = data?.eventByName?.info.safetyRating === SafetyRating.unsafeRequiresEscalation;

  return {
    isUnsafe,
    isLoading: loading,
  };
};
